import { formatQuantity, type UnitId } from "@bl/domain";
import { Link } from "@tanstack/react-router";
import { Fragment, useEffect, useId, useRef, useState } from "react";
import { type ShortfallItem, useBrews, useShortfall } from "../api";
import { ErrorNote, Field, Toolbar, usePageTitle } from "../components/ui";
import { showDate, today } from "../format";
import { useLayout } from "../layout/LayoutProvider";
import { addDays, daysBetween } from "./Schedule";

/**
 * What to order for the brews planned ahead, by supplier, with the day each item is first needed
 * (PLAN.md, "Slice 2"), on a page under Inventory beside Invoices (Tyler, 2026-10-19). How far ahead it
 * looks is kept per device.
 */
export function ToOrderPage() {
  usePageTitle("To order");
  const { layout, update } = useLayout();
  const s = layout.schedule;
  const now = today();
  return (
    <>
      <Toolbar />
      <div className="page-body centered">
        <h1 className="page-title">To order</h1>
        <p className="muted" style={{ marginTop: -8 }}>
          What the planned brews need beyond what is on hand, grouped by supplier. Nothing comes out
          of inventory until a brew is brewed.
        </p>
        <ThroughPicker
          window={s}
          now={now}
          onChange={(patch) => update((l) => ({ ...l, schedule: { ...l.schedule, ...patch } }))}
        />
        <OrderList through={addDays(now, s.ahead)} />
      </div>
    </>
  );
}

/** To order looks this far ahead: a preset, or Custom, which opens one date. */
const AHEAD: { days: number; label: string }[] = [
  { days: 14, label: "2 weeks" },
  { days: 31, label: "1 month" },
  { days: 183, label: "6 months" },
  { days: 365, label: "1 year" },
];

const qty = (canonical: string, unit: UnitId) => formatQuantity(canonical, { unit });

type OrderWindow = ReturnType<typeof useLayout>["layout"]["schedule"];

/**
 * How far ahead To order looks: the presets and Custom in one row. Custom's date flies out from its
 * button on a desktop and sits centered under the row on a phone (Tyler, 2026-10-02). Once closed on a
 * desktop, the button says the date it is set to.
 */
function ThroughPicker({
  window: s,
  now,
  onChange,
}: {
  window: OrderWindow;
  now: string;
  onChange: (patch: Partial<OrderWindow>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  const through = addDays(now, s.ahead);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: globalThis.PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="schedule-window-wrap" ref={ref}>
      <fieldset className="segmented schedule-window">
        <legend className="sr-only">How far ahead to order for</legend>
        {AHEAD.map((a) => (
          <button
            key={a.days}
            type="button"
            aria-pressed={!s.custom && s.ahead === a.days}
            onClick={() => {
              setOpen(false);
              onChange({ ahead: a.days, custom: false });
            }}
          >
            {a.label}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={s.custom}
          aria-expanded={open}
          aria-controls={id}
          onClick={() => {
            setOpen((o) => !s.custom || !o);
            onChange({ custom: true });
          }}
        >
          Custom
          {s.custom ? <span className="schedule-range">: through {showDate(through)}</span> : null}
        </button>
      </fieldset>
      {s.custom ? (
        <div id={id} className={`schedule-dates ${open ? "open" : ""}`}>
          <Field label="Through">
            <input
              type="date"
              value={through}
              min={now}
              onChange={(e) => {
                if (e.target.value)
                  onChange({ ahead: Math.max(0, daysBetween(now, e.target.value)) });
              }}
            />
          </Field>
        </div>
      ) : null}
    </div>
  );
}

/** A line of a planned brew To order cannot count. */
type Uncounted = NonNullable<ReturnType<typeof useShortfall>["data"]>["uncounted"][number];

function OrderList({ through }: { through: string }) {
  const { data, isLoading, error } = useShortfall(through);
  const { data: brews = [] } = useBrews();
  const items = data?.items ?? [];
  // One line per thing to check, with each brew it is for (the re-critique, 2026-10-07: "Clean Ale,
  // for Porch Pale Ale Oct 7" was listed twice, for two brews, and once more for every brew after).
  const inProgress = new Set(brews.filter((b) => b.status === "draft").map((b) => b.id));
  const toCheck = new Map<string, { u: Uncounted; brews: { id: string; on: string }[] }>();
  for (const u of data?.uncounted ?? []) {
    const key = `${u.name}|${u.recipeName}|${u.reason}`;
    const line = toCheck.get(key) ?? { u, brews: [] };
    line.brews.push({ id: u.brewId, on: u.on });
    toCheck.set(key, line);
  }
  // An order is a basket per supplier (Scott, competitive-pain.md #5), so group by who it comes from.
  const bySupplier = new Map<string, ShortfallItem[]>();
  for (const i of items) {
    const who = i.supplier ?? "No supplier yet";
    bySupplier.set(who, [...(bySupplier.get(who) ?? []), i]);
  }
  return (
    <div className="schedule-order">
      <ErrorNote error={error} />
      {isLoading ? <p className="muted">Loading</p> : null}
      {data && items.length === 0 ? (
        <p className="schedule-ready">
          Everything To order counts through {showDate(through)} is on hand.
        </p>
      ) : null}
      {[...bySupplier].map(([who, list]) => (
        <section key={who}>
          <h2 className="brew-heading">{who}</h2>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">To order</th>
                  <th>Needed by</th>
                  <th className="num">On hand</th>
                  <th className="num">Planned</th>
                  <th>For</th>
                </tr>
              </thead>
              <tbody>
                {list.map((i) => (
                  <tr key={i.item.id}>
                    <td>
                      <Link to="/items/$itemId" params={{ itemId: i.item.id }}>
                        {i.item.name}
                      </Link>
                    </td>
                    <td className="num">
                      <strong>{qty(i.toOrder, i.item.displayUnit)}</strong>
                    </td>
                    <td>{showDate(i.neededBy)}</td>
                    <td className="num">{qty(i.onHand, i.item.displayUnit)}</td>
                    <td className="num">{qty(i.planned, i.item.displayUnit)}</td>
                    <td className="muted schedule-for">
                      {i.brews.map((b) => (
                        <span key={b.id}>
                          <Link to="/brews/$brewId" params={{ brewId: b.id }}>
                            {b.recipeName}
                          </Link>{" "}
                          {showDate(b.on)}
                          {Number(b.short) > 0 ? (
                            <span className="schedule-short">
                              , short {qty(b.short, i.item.displayUnit)}
                            </span>
                          ) : null}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
      {data?.uncounted.length ? (
        <>
          <h2 className="brew-heading">Check by hand</h2>
          <p className="faint">
            Lines To order cannot count, so nothing above covers them: not linked to an item, or
            with no amount it can work out.
          </p>
          <ul className="schedule-uncounted">
            {[...toCheck.entries()].map(([key, { u, brews: those }]) => (
              <li key={key}>
                {u.name} ({u.reason === "not_linked" ? "not linked" : "no amount"}), for{" "}
                {u.recipeName}:{" "}
                {those.map((b, n) => (
                  <Fragment key={b.id}>
                    {n ? ", " : ""}
                    <Link to="/brews/$brewId" params={{ brewId: b.id }}>
                      {inProgress.has(b.id) ? "in progress" : showDate(b.on)}
                    </Link>
                  </Fragment>
                ))}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
